import { generateAutoGeneratedCurvePanel } from '@experiment-management-shared';
import { CURVES_SECTION_NAME } from '@experiment-management-shared/constants';
import { Panel, PanelSection } from '@experiment-management-shared/types';
import { QueryConfig } from '@shared/api';
import { useQuery } from 'react-query';
import isEmpty from 'lodash/isEmpty';
import { LayoutWorker } from '@experiment-management-shared/components/Charts/workers';
import useExperimentsCurveNames from '@API/project/useExperimentsCurveNames';

const getCurveNamesForExperiments = async (
  curveNames: string[],
  experimentKeysCount: number,
  existingSections?: PanelSection[] | null
) => {
  const destinationSectionName = CURVES_SECTION_NAME;
  const existedSection = existingSections?.find(
    s => s.title === destinationSectionName
  );

  let autoGeneratedPanels: Panel[] = [];

  if (!isEmpty(curveNames)) {
    autoGeneratedPanels = curveNames.map(name =>
      generateAutoGeneratedCurvePanel(name, experimentKeysCount)
    );
  }

  const section = await LayoutWorker.generateAutogeneratedPanelsSection({
    autoGeneratedPanels,
    destinationSectionName,
    existedSection
  });

  return [section];
};

interface AutogenCurvesSectionsOpts {
  experimentKeys: string[];
  existingSections?: PanelSection[] | null;
}

const useAutogenCurvesSections = (
  { experimentKeys, existingSections }: AutogenCurvesSectionsOpts,
  config: QueryConfig<PanelSection[]>
) => {
  const {
    data: curveNames = [],
    isSuccess: isNamesSuccess,
    isFetching: isNamesFetching,
    isLoading: isNamesLoading
  } = useExperimentsCurveNames(
    {
      experimentKeys
    },
    {
      enabled: config.enabled
    }
  );

  const {
    isFetching: isLayoutFetching,
    isSuccess: isLayoutSuccess,
    isLoading: isLayoutLoading
  } = useQuery(
    ['auto-generated-curve-sections', { experimentKeys, curveNames }],
    () =>
      getCurveNamesForExperiments(
        curveNames,
        experimentKeys.length,
        existingSections
      ),
    {
      ...config,
      enabled: config.enabled && isNamesSuccess && !isNamesFetching
    }
  );

  return {
    isSuccess: isNamesSuccess && isLayoutSuccess,
    isFetching: isLayoutFetching || isNamesFetching,
    isLoading: isLayoutLoading || isNamesLoading
  };
};

export default useAutogenCurvesSections;
